import {
  Autocomplete,
  FormControl,
  Grid,
  ListItem,
  ListItemText,
  Typography,
} from "@mui/material";
import { isFulfilled } from "@reduxjs/toolkit";
import { useEffect, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../../../../app/hooks";
import isEmpty from "../../../../../../common/helpers/isEmpty";
import { VerticalAlignGrid } from "../../../../../invoices/styled";
import { WorkTitle } from "../../../../../orders/orderAPI";
import { fetchWorkTitles } from "../../../../../workTitles/workTitleSlice";
import CommonTextField from "../../../../../../common/components/functional/commonTextField/CommonTextField";
import { StyledFormSection } from "../../../styles";

type Props = {
  disabled?: boolean;
  existingWorkTitles: string[];
};

const WorkTitleSection = ({ disabled, existingWorkTitles }: Props) => {
  const { i18n, t } = useTranslation("common");
  const { control } = useFormContext();
  const { workTitle } = useAppSelector((state) => state.localized);
  const dispatch = useAppDispatch();
  const [availableWorkTitles, setAvailableWorkTitles] = useState<string[]>([]);

  useEffect(() => {
    if (isEmpty(availableWorkTitles)) {
      const language = i18n.language.substring(0, 2);
      dispatch(fetchWorkTitles({ language })).then((action) => {
        if (isFulfilled(action)) {
          const workTitlesArray: WorkTitle[] = action.payload;
          setAvailableWorkTitles(workTitlesArray.map((wt) => wt.id));
        }
      });
    }
  }, [availableWorkTitles, dispatch, i18n.language]);

  return (
    <Grid marginTop="0.5rem">
      <StyledFormSection>
        <VerticalAlignGrid container spacing={1}>
          <Grid
            container
            item
            xs={3}
            justifyContent="flex-end"
            paddingRight="0.5rem"
          >
            <Typography variant="secondary">{t("common:workTitle")}</Typography>
          </Grid>
          <Grid item xs={5}>
            <FormControl fullWidth size="small">
              <Controller
                name="workTitleId"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Autocomplete
                    fullWidth
                    value={value}
                    disabled={disabled}
                    getOptionLabel={(workTitleId) => workTitle[workTitleId]}
                    options={availableWorkTitles}
                    getOptionDisabled={(workTitleId) =>
                      existingWorkTitles.includes(workTitleId)
                    }
                    renderOption={(props, workTitleId) => (
                      <ListItem {...props} key={workTitleId}>
                        <ListItemText>{workTitle[workTitleId]}</ListItemText>
                      </ListItem>
                    )}
                    renderInput={(params) => (
                      <CommonTextField
                        {...params}
                        name="workTitle"
                        label={disabled ? "" : t("common:searchWorkTitles")}
                      />
                    )}
                    onChange={(_, newWorkTitleId: string | null) => {
                      onChange(newWorkTitleId);
                    }}
                  />
                )}
              />
            </FormControl>
          </Grid>
        </VerticalAlignGrid>
      </StyledFormSection>
    </Grid>
  );
};

export default WorkTitleSection;
